import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatNumber, formatDate, formatCurrency } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router-dom';
import '../styles/bitacora.css';
import '../styles/sales.css';
import '../styles/saleStatusBadges.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import SaleDetailModal from '../components/Sales/SaleDetailModal';
import { TableCard } from '../components/ui';
import { getSaleDisplayStatusLabel, getSaleDisplayStatusClass } from '../utils/saleStatusAdapter';
import { trackPosSessionOpened } from '../analytics/events';
import { useAnalyticsContext } from '../analytics/useAnalyticsContext';
import * as XLSX from 'xlsx';

const MySwal = withReactContent(Swal);

const getCssToken = (tokenName) => {
  return window.getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();
};

const SalesPage = () => {
  const navigate = useNavigate();
  const { commerceId, branchId, channel } = useAnalyticsContext();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    search: '',
    ui_status: '',
    from_date: '',
    to_date: ''
  });
  const [uiStatusOptions, setUiStatusOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');

  // Track POS session opened when entering sales module
  useEffect(() => {
    if (commerceId) {
      trackPosSessionOpened({
        commerceId,
        branchId,
        channel
      });
    }
  }, [commerceId, branchId, channel]);

  useEffect(() => {
    fetchSales();
  }, [filters, pagination.current_page]);

  // Solo enviar búsqueda a la API con 0 o >= 3 caracteres (backend exige min:3)
  useEffect(() => {
    const initialSearch = searchInput.length === 0 && !filters.search;
    if (!initialSearch && (searchInput.length === 0 || searchInput.length >= 3)) {
      setFilters(prev => ({
        ...prev,
        search: searchInput
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  }, [searchInput]);

  const getSearchValue = (value) => {
    return (value.length === 0 || value.length >= 3) ? value : '';
  };

  const buildSalesParams = (page, perPage = pagination.per_page) => {
    const params = {
      search: getSearchValue(filters.search),
      ui_status: filters.ui_status || '',
      from_date: filters.from_date || '',
      to_date: filters.to_date || '',
      page,
      per_page: perPage
    };
    return new URLSearchParams(params);
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = buildSalesParams(pagination.current_page, pagination.per_page);

      const response = await api.get(`/sales?${params}`);
      setSales(response.data.sales);
      setPagination(response.data.pagination);
      if (response.data.filters?.ui_status_options?.length) {
        setUiStatusOptions(response.data.filters.ui_status_options);
      }
    } catch (error) {
      console.error('Error al obtener ventas:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las ventas',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setSearchInput(value);
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  };

  const handleCancelSale = async (saleId) => {
    MySwal.fire({
      title: '¿Estás seguro?',
      text: "No podrás revertir esta acción",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: getCssToken('--rg-color-danger-base'),
      cancelButtonColor: getCssToken('--rg-color-secondary-base'),
      confirmButtonText: 'Sí, anular venta',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post(`/sales/${saleId}/cancel`);
          fetchSales();
          MySwal.fire(
            'Anulada',
            'La venta ha sido anulada',
            'success'
          );
        } catch (error) {
          MySwal.fire(
            'Error',
            'No se pudo anular la venta',
            'error'
          );
        }
      }
    });
  };

  const handleUploadDocuments = (saleId) => {
    // Implementar lógica de carga de documentos
  };

  const handlePayInstallment = (saleId, installmentId) => {
    MySwal.fire({
      title: '¿Deseas registrar el pago de esta cuota?',
      text: "Confirma para proceder con el pago",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: getCssToken('--rg-color-success-base'),
      cancelButtonColor: getCssToken('--rg-color-secondary-base'),
      confirmButtonText: 'Sí, pagar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post(`/sales/${saleId}/installments/${installmentId}/pay`);
          const response = await api.get(`/sales/${saleId}`);
          setSelectedSale(response.data);
          fetchSales(); // Actualizar la lista de ventas
          MySwal.fire(
            'Pagado',
            'La cuota ha sido pagada correctamente',
            'success'
          );
        } catch (error) {
          MySwal.fire(
            'Error',
            'No se pudo procesar el pago de la cuota',
            'error'
          );
        }
      }
    });
  };



  const handleViewDetail = (saleId, customerId) => {
    setSelectedSale({ id: saleId, customer: { id: customerId } });
    setShowDetailModal(true);
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  const handleExportExcel = async () => {
    if (exporting) return;

    setExporting(true);
    try {
      const perPage = pagination.per_page || 25;
      const firstResponse = await api.get(`/sales?${buildSalesParams(1, perPage)}`);
      const firstPageSales = firstResponse?.data?.sales || [];
      const firstPagination = firstResponse?.data?.pagination || {};
      const totalSales = Number(firstPagination.total || 0);
      const lastPage = Number(firstPagination.last_page || 1);

      if (totalSales === 0 || firstPageSales.length === 0) {
        MySwal.fire({
          title: 'Sin datos',
          text: 'No hay ventas para exportar con los filtros actuales.',
          icon: 'info'
        });
        return;
      }

      let allSales = [...firstPageSales];
      for (let page = 2; page <= lastPage; page += 1) {
        const response = await api.get(`/sales?${buildSalesParams(page, perPage)}`);
        allSales = allSales.concat(response?.data?.sales || []);
      }

      const rows = allSales.map((sale) => ({
        Venta: `#${sale.id}`,
        Cliente: sale.customer?.name || '',
        DNI: sale.customer?.dni || '',
        Fecha: formatDate(sale.sale_date),
        Vendedor: sale.seller?.full_name || '',
        Monto: formatCurrency(sale.amount),
        Cuotas: sale.installments,
        Estado: getSaleDisplayStatusLabel(sale)
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas');

      const dateString = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `Ventas_${dateString}.xlsx`);
    } catch (error) {
      console.error('Error al exportar ventas:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudo exportar el archivo de ventas.',
        icon: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Componente Skeleton para la tabla
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index}>
          <td><Skeleton width={40} height={30} /></td>
          <td><Skeleton width={80} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td className="text-center">
            <Skeleton width={50} height={25} />
          </td>
          <td>
            <div className="d-flex gap-2">
              <Skeleton width={30} height={30} />
              <Skeleton width={30} height={30} />
              <Skeleton width={30} height={30} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  // Componente de Paginación
  const Pagination = ({ loading }) => (
    <nav aria-label="Navegación de páginas">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, i) => (
          <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}>
            <button 
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
              disabled={loading}
            >
              {i + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button 
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3>Ventas</h3>
          </div>
        </div>
      </div>

      <section className="section">
        <TableCard
          toolbar={
            <>
              <div className="row">
                <div className="col-md-3 mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por cliente o DNI..."
                    name="search"
                    value={searchInput}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <select
                    className="form-select"
                    name="ui_status"
                    value={filters.ui_status}
                    onChange={handleFilterChange}
                  >
                    <option value="">Todos los estados</option>
                    {uiStatusOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-2">
                  <input
                    type="date"
                    className="form-control"
                    name="from_date"
                    value={filters.from_date}
                    onChange={handleFilterChange}
                    placeholder="Fecha desde"
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <input
                    type="date"
                    className="form-control"
                    name="to_date"
                    value={filters.to_date}
                    onChange={handleFilterChange}
                    placeholder="Fecha hasta"
                  />
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-12 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={handleExportExcel}
                    disabled={loading || exporting}
                    title="Exportar a Excel según filtros actuales"
                    aria-label="Exportar a Excel según filtros actuales"
                  >
                    <i className="bi bi-file-earmark-spreadsheet me-2" aria-hidden></i>
                    {exporting ? 'Exportando...' : 'Exportar a Excel'}
                  </button>
                </div>
              </div>
            </>
          }
          table={
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Venta</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Vendedor</th>
                  <th>Monto</th>
                  <th>Cuotas</th>
                  <th className="text-center">Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-5">
                      <i className="bi bi-inbox rg-empty-icon-lg"></i>
                      <p className="mt-3 mb-0">No hay ventas para mostrar</p>
                    </td>
                  </tr>
                ) : (
                  sales.map((sale) => (
                    <tr key={sale.id}>
                      <td>#{sale.id}</td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            handleGoToCustomer(sale.customer.dni);
                          }}
                          className="rg-link-button"
                        >
                          {sale.customer.name}
                        </button>
                        <br />
                        <small className="text-muted">
                          DNI: {sale.customer.dni}
                        </small>
                      </td>
                      <td>{formatDate(sale.sale_date)}</td>
                      <td>{sale.seller.full_name}</td>
                      <td>{formatCurrency(sale.amount)}</td>
                      <td className="text-center">{sale.installments}</td>
                      <td className="text-center">
                        <span
                          className={`sale-status-badge ${getSaleDisplayStatusClass(sale)}`}
                        >
                          {getSaleDisplayStatusLabel(sale)}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewDetail(sale.id, sale.customer.id)}
                            title="Ver detalle"
                          >
                            <i className="bi bi-eye me-1"></i>
                            Ver
                          </button>
                          {sale.status !== 'annulled' && false && ( // TODO: implement sales anullment
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleCancelSale(sale.id)}
                              title="Anular venta"
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Anular
                            </button>
                          )}
                          {/* <button 
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleUploadDocuments(sale.id)}
                            title="Documentos"
                          >
                            <i className="bi bi-file-earmark-text"></i>
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          }
          pagination={<Pagination loading={loading} />}
        />

        {showDetailModal && (
          <SaleDetailModal
            saleId={selectedSale.id}
            customerId={selectedSale.customer.id}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedSale(null);
            }}
          />
        )}
      </section>
    </div>
  );
};

export default SalesPage; 